import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetDoctolibImportQuery } from '@/hooks/queries/use-get-doctolib-import';
import { formatEuros } from '@/lib/format-currency';
import {
  useViewDoctolibImportModalViewModel,
  ViewDoctolibImportModalApi,
} from './view-doctolib-import-modal.viewmodel';

export const ViewDoctolibImportModal = ({
  doctolibImportId,
  onClose,
}: ViewDoctolibImportModalApi) => {
  const { t } = useTranslation();
  const viewModel = useViewDoctolibImportModalViewModel({
    api: { onClose },
  });

  const { data, isLoading } = useGetDoctolibImportQuery(doctolibImportId);

  const items = data?.workScheduleDoctolibData ?? [];
  const totalAmountDue = items.reduce(
    (sum, item) => sum + item.totalAmountDue,
    0,
  );
  const totalAmountPaid = items.reduce(
    (sum, item) => sum + item.totalAmountPaid,
    0,
  );

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) viewModel.api.close();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{t('view-doctolib-import-modal.title')}</DialogTitle>
          {data && (
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-sm">
                {format(parseISO(data.periodFrom), 'dd/MM/yyyy')} –{' '}
                {format(parseISO(data.periodTo), 'dd/MM/yyyy')}
              </span>
              <span className="text-muted-foreground text-sm">
                {t('view-doctolib-import-modal.subtitle')}
              </span>
            </div>
          )}
        </DialogHeader>

        {isLoading && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            {t('view-doctolib-import-modal.loading')}
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            {t('view-doctolib-import-modal.empty')}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t('view-doctolib-import-modal.column.invoice-identifier')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('view-doctolib-import-modal.column.amount-due')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('view-doctolib-import-modal.column.amount-paid')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={`${item.invoiceIdentifier}-${index}`}>
                    <TableCell>{item.invoiceIdentifier}</TableCell>
                    <TableCell className="text-right">
                      {formatEuros(item.totalAmountDue)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatEuros(item.totalAmountPaid)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold">
                    {t('view-doctolib-import-modal.total')}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatEuros(totalAmountDue)}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatEuros(totalAmountPaid)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={viewModel.api.close}
          >
            {t('btn.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
